/**
 * 事件星云（design §11.11，P4a）：连着几天都有照片时，这件事外面罩一片淡雾——贴着盘面、
 * 沿首末两天方向拉长的椭圆，软边里两层值噪声慢慢流动，颜色是这件事照片主色的平均往淡蓝紫拉一半。
 * 一件事一个面片，一个实例化图层一次画完；加法混合叠在星底下，很淡（线性亮度 0.05–0.10，呼吸 ±15%），
 * 只是画，不参与点选。雾摆哪、什么颜色在 events.ts 算；显多强（镜头远近、星座、看照片、伪装）在 galaxy.ts。
 */
import * as THREE from 'three';
import type { NebulaPatch } from './events.ts';

const VERT = /* glsl */ `
attribute vec3 aPos;
attribute vec2 aAxis;
attribute vec2 aSize;
attribute vec3 aColor;
attribute float aSeed;
varying vec2 vUv;
varying vec2 vFog;
varying vec3 vColor;
varying float vSeed;

void main() {
  vec2 u = position.xy * 2.0;
  // 面片平躺在盘面上（不面向镜头）：长轴沿首末两天的连线，短轴是横过星团的方向
  vec2 xz = aPos.xz + aAxis * (u.x * aSize.x) + vec2(-aAxis.y, aAxis.x) * (u.y * aSize.y);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(xz.x, aPos.y, xz.y, 1.0);
  vUv = u;
  // 噪声取世界坐标：相邻两片雾的纹路连续，也免得长椭圆把噪声拉成条
  vFog = xz;
  vColor = aColor;
  vSeed = aSeed;
}
`;

const FRAG = /* glsl */ `
uniform float uTime;
uniform float uOpacity;
varying vec2 vUv;
varying vec2 vFog;
varying vec3 vColor;
varying float vSeed;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}

void main() {
  float r = length(vUv);
  if (r >= 1.0) discard;
  // 两层慢慢流动的噪声：大片明暗 + 细一点的丝缕；每个实例一个相位，各片的呼吸错开
  float n = noise(vFog * 0.28 + vec2(uTime * 0.05, -uTime * 0.03) + vSeed * 19.0)
          + 0.5 * noise(vFog * 0.7 + vec2(-uTime * 0.04, uTime * 0.055) + vSeed * 31.0);
  float breathe = 1.0 + 0.15 * sin(uTime * 0.45 + vSeed * 6.2831853);
  vec3 col = vColor * exp(-r * r * 3.5) * (0.4 + 0.6 * (n / 1.5)) * breathe * uOpacity;
  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
}
`;

const QUAD = new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]);

export class EventNebula {
  readonly mesh: THREE.Mesh<THREE.InstancedBufferGeometry, THREE.ShaderMaterial>;
  readonly uniforms = {
    uTime: { value: 0 },
    uOpacity: { value: 0 },
  };

  constructor() {
    const material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      // 平躺的面片从上往下看是背面（单位方形摆到盘面上以后三角形的绕向反了），默认只画正面会整片看不见
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(this.build([]), material);
    this.mesh.frustumCulled = false;
    // 和星点同一批画（在星轨、星茧、连线之前）：雾是衬在底下的，加法混合交换顺序结果一样
    this.mesh.renderOrder = 1;
    this.mesh.visible = false;
  }

  private build(list: readonly NebulaPatch[]): THREE.InstancedBufferGeometry {
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(QUAD, 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    const n = Math.max(1, list.length);
    const pos = new Float32Array(n * 3), axis = new Float32Array(n * 2), size = new Float32Array(n * 2);
    const color = new Float32Array(n * 3), seed = new Float32Array(n);
    list.forEach((p, k) => {
      pos.set([p.x, p.y, p.z], k * 3);
      axis.set([p.ax, p.az], k * 2);
      size.set([p.major, p.minor], k * 2);
      color.set([p.r, p.g, p.b], k * 3);
      seed[k] = p.seed;
    });
    geo.setAttribute('aPos', new THREE.InstancedBufferAttribute(pos, 3));
    geo.setAttribute('aAxis', new THREE.InstancedBufferAttribute(axis, 2));
    geo.setAttribute('aSize', new THREE.InstancedBufferAttribute(size, 2));
    geo.setAttribute('aColor', new THREE.InstancedBufferAttribute(color, 3));
    geo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 1));
    geo.instanceCount = list.length;
    return geo;
  }

  /** 整个换掉（事件不多，每次重建） */
  set(list: readonly NebulaPatch[]): void {
    const old = this.mesh.geometry;
    this.mesh.geometry = this.build(list);
    old.dispose();
  }

  /** 每帧调：time 喂给噪声和呼吸（减少动态效果时喂 0，雾还在、只是不流动）；opacity 0–1 */
  update(time: number, opacity: number): void {
    this.uniforms.uTime.value = time;
    this.uniforms.uOpacity.value = opacity;
    this.mesh.visible = opacity > 0.002 && this.mesh.geometry.instanceCount > 0;
  }
}
